<script setup lang="ts">
import { toRefs, watch, computed, ref, inject } from 'vue';
import { addImageScaler, compareSortingValue } from '@/core';
import {
  HomePlanImage,
  LotCountNeighborhood,
  PlanNeighborhoodList,
} from './../types';
import Modal from './modal/ImageModal.vue';
import ImageLightbox from './carousel/ImageLightbox.vue';
import LazyImage from './LazyImage.vue';
import LocationIcon from '@/assets/icons/Location.vue';

const props = defineProps<{
  homePlan: any;
  qmiAndHomeplans: any[];
  selectedLotId: string;
  neighborhoodId: string;
  isKioskMode: boolean;
  selectedNeighborhoodIds: string[];
  lotsCount: LotCountNeighborhood[];
}>();

const {
  homePlan,
  qmiAndHomeplans,
  selectedLotId,
  selectedNeighborhoodIds,
  lotsCount,
} = toRefs(props);

let planClicks = {};
const emit = defineEmits(['voidHomePlanId']);

// injector
const isSalesOrLandView = inject('setSelectionDropdown', ref(false));

const availableLotsPresent = computed(() => {
  if (selectedNeighborhoodIds.value.length) {
    const count = lotsCount.value.find(
      (neighborhoodLot) =>
        neighborhoodLot.neighborhoodId === selectedNeighborhoodIds.value[0]
    )?.available;
    return !!count && count > 0;
  } else {
    let count = 0;
    for (const neighborhoodLot of lotsCount.value) {
      if (neighborhoodLot?.available) {
        count += neighborhoodLot.available;
      }
    }
    return !!count && count > 0;
  }
});
const changePlanClicks = () => {
  console.log('changePlanClicks');
  let isPlanClicked = false;
  Object.keys(planClicks).forEach((planId) => {
    qmiAndHomeplans.value.forEach((plan) => {
      let data = !plan.job_number ? plan.plan_id : plan.job_number;
      if (data === planId) {
        isPlanClicked = true;
      }
    });
  });
  if (!isPlanClicked) {
    for (const key in planClicks) delete planClicks[key];
  }
};

const BYNDER_CONTENT_TYPE = 'bynder';

const firstBynderAssetUrl = (images: HomePlanImage[] | undefined) =>
  images?.find((image) => image.content_type_name === BYNDER_CONTENT_TYPE)
    ?.asset_value;

// `thumbnail_img` is present on plans and absent on QMIs, but the API also sends
// it as an empty string for plans that have no neighborhood assets — so fall
// back to the plan's first Bynder photo, then to the QMI `image` array.
const getPlanImageUrl = (homePlan: any) =>
  homePlan.thumbnail_img ||
  firstBynderAssetUrl(homePlan.images) ||
  homePlan.image?.[0] ||
  '';

const formatDate = (moveInDate: string) => {
  let date = new Date(moveInDate);
  let formattedDate = date.toDateString().split(' ');
  return `${formattedDate[1]} ${formattedDate[2]}, ${formattedDate[3]}`;
};

const formatPrice = (value: number) => {
  var formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
  return formatter.format(value);
};

const addScaler = (url: string) => addImageScaler(url);

const sortNeighborhoods = (neighborhoods: PlanNeighborhoodList[]) => {
  const list = [] as PlanNeighborhoodList[];
  Object.assign(list, neighborhoods);
  return list.sort((first, second) =>
    compareSortingValue(first.neighborhood_name, second.neighborhood_name)
  );
};
const lightboxIdx = ref(-1);
const activeImages = ref([] as any[]);
const openLightbox = (
  event,
  images,
  qmiImages,
  thumbnailImage: string,
  index
) => {
  if (isSalesOrLandView.value && (images?.length > 0 || thumbnailImage)) {
    emit('voidHomePlanId');
    event?.stopPropagation?.();
    activeImages.value = [];
    if (images?.length) {
      activeImages.value = images
        .filter((img) => img.content_type_name === 'bynder')
        .map((img) => ({
          asset_value: img.asset_value,
          alt_tag: img.alt_tag ?? '',
          caption: img.caption ?? '',
        }));
    } else if (qmiImages?.length) {
      activeImages.value = qmiImages.map((img) => ({
        asset_value: img,
        alt_tag: '',
        caption: '',
      }));
    } else if (thumbnailImage) {
      activeImages.value = [
        {
          asset_value: thumbnailImage,
          alt_tag: '',
          caption: '',
        },
      ];
    }
    lightboxIdx.value = index;
  }
};

const closeLightbox = () => {
  lightboxIdx.value = -1;
  activeImages.value = [];
};
watch(selectedLotId, (newVal) => {
  if (newVal) {
    planClicks = {};
  }
});

watch(qmiAndHomeplans, () => {
  changePlanClicks();
});
</script>

<template>
  <div class="flex flex-col gap-10">
    <div class="flexDiv" v-if="homePlan.price_low_with_premium >= 0">
      <h4 class="sideplan-list-title">
        {{ homePlan.plan_name }}
      </h4>
      <p
        class="price-block"
        v-if="homePlan.price_low_with_premium && availableLotsPresent"
      >
        <span class="price-block-text">Starts at</span>
        <span class="price-block-amount">
          {{ formatPrice(homePlan.price_low_with_premium) }}
        </span>
      </p>
      <p class="price-block" v-else>
        <span class="price-block-text">Price not available at this time</span>
      </p>
    </div>

    <ul class="fw-ic gap-1" v-if="homePlan.price_low_with_premium >= 0">
      <li
        class="location-block"
        v-for="neighborhood in sortNeighborhoods(homePlan.neighborhoods)"
        :key="neighborhood.neighborhood_code"
      >
        <i class="location-block-icon">
          <LocationIcon />
        </i>
        <span class="location-block-title">{{
          neighborhood.neighborhood_name
        }}</span>
      </li>
    </ul>

    <!-- address block -->
    <ul v-if="homePlan.price >= 0">
      <li
        :class="[
          !homePlan.isFuturePlan &&
          homePlan.status?.toLowerCase() !== 'future home'
            ? 'qmi-card-detail'
            : 'flexDiv gap-1',
        ]"
      >
        <span class="location-block">
          <span class="location-block-address">
            <i class="location-block-icon">
              <LocationIcon />
            </i>
            <span class="location-block-title">{{ homePlan.address }} </span>
          </span>
          <span
            class="location-block-date location-block-date-aside"
            v-if="homePlan.move_in_date"
          >
            Move-in date:
            {{
              new Date() >= new Date(homePlan.move_in_date)
                ? 'Immediate'
                : formatDate(homePlan.move_in_date)
            }}
          </span>
        </span>
        <p
          class="price-block"
          v-if="
            homePlan.price > 0 &&
            !(
              homePlan.isFuturePlan ||
              homePlan.status?.toLowerCase() === 'future home'
            )
          "
        >
          <span class="price-block-amount ml-auto">
            {{ formatPrice(homePlan.price) }}
          </span>
        </p>
      </li>
    </ul>

    <!-- plan details -->
    <ul class="fw-ic gap-2" v-if="homePlan.price >= 0">
      <li class="sideplan-list-title">
        {{ `${homePlan.plan_market_name} ${homePlan.elevation}` }}
      </li>
      <li class="location-block">
        <i class="location-block-icon">
          <LocationIcon />
        </i>
        <span class="location-block-title">{{
          homePlan.neighborhood_name
        }}</span>
      </li>
      <li
        class="btn qmi"
        :style="{ 'background-color': homePlan.color }"
        v-if="homePlan.isFuturePlan ||
              homePlan.status?.toLowerCase() === 'future home'"
      >
        Future Home
      </li>
      <li
        class="btn qmi"
        :style="{ 'background-color': homePlan.color }"
        v-else
      >
        Quick Move-in
      </li>
    </ul>

    <!-- image -->
    <div class="sideplan-list-img">
      <LazyImage
        :src="addScaler(getPlanImageUrl(homePlan))"
        :alt="homePlan.plan_name"
        :isNeighborhoodImage="false"
        @click="
          (e) =>
            openLightbox(
              e,
              homePlan.images,
              homePlan.image,
              getPlanImageUrl(homePlan),
              0
            )
        "
      />
      <Modal
        v-if="lightboxIdx > -1"
        :default-state-of-modal="true"
        :dialog-pannel-classes="'asset-lightbox-modal'"
        :modal-outer-container-classes="'xxxxl:max-w-[1248px] mx-auto'"
        @modal-closed="closeLightbox"
      >
        <template #modalContent>
          <ImageLightbox
            :slider-data="activeImages"
            :current-slide="lightboxIdx"
          />
        </template>
      </Modal>
    </div>
  </div>
</template>

<style lang="scss">
.sideplan-list {
  &-wrapper {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
  }

  display: flex;
  flex-direction: column;
  row-gap: 10px;
  background-color: var(--cardBg, $white);
  min-height: 217px;
  border: 3px solid var(--cardBgBdr, transparent);
  cursor: pointer;
  border-radius: 4px;

  &.is-selected {
    --cardBg: var(--commCardBgSelected);
    --cardBgBdr: #{$lime-green-new};
    box-shadow: 0px -2px 7px #434805;
  }

  &-title {
    @include font(12px, 700);
    text-decoration-line: underline;
    color: $gray-600;
  }

  &-img {
    position: relative;
    cursor: pointer;

    .map-card-imgbanner {
      img {
        --mapCardImgHeight: 130px;
      }
    }

    &.is-selected {
      border: 3px solid $primary;
    }
  }

  .qmi {
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    line-height: 14px;
    padding: 1px 10px;
    min-width: unset;
    margin-left: auto;
  }
}

.no-results {
  text-align: center;
  color: $white;
  @include font(14px, 400);
  text-transform: capitalize;
  margin-top: 60px;
}

.removeUnderline {
  text-decoration: none;
}

.asset-lightbox-modal {
  @apply xxxl:max-w-[1328px];
  .custom-slider-slide {
    @apply flex items-center justify-center h-full;
  }

  .custom-slider {
    transition: transform 0.5s ease-in-out;
    @apply flex;
  }
  .carousel__prev,
  .carousel__next {
    @apply hidden lg:inline-block m-0;
  }
  .custom-slider-holder {
    @apply w-full h-full;
    .carousel__item {
      @apply h-full;
      & > div {
        @apply h-full flex items-center;
      }
    }
  }
  .masonry-thumbnail {
    img {
      @apply cursor-pointer;
    }
    .carousel__item {
      div {
        @apply h-full;
      }
    }
  }

  .modal-body {
    & > div {
      @apply h-auto lg:h-[calc(100dvh-280px)];
    }
  }
}
</style>
